import React from 'react';
import ProgressLine from '../ProgressLine/ProgressLine.inner';
import PropTypes from 'prop-types';
import * as S from './Step.styles';
import {
  PROGRESS_STATE,
  STEP_STATE,
  STEP_TYPE,
} from '../../../utils/Constants';
import P from '../../Texts/P/P';
import Icon from '../../Icon/Icon';

const TextLineSplitter = ({ text }) =>
  text.split('\n').map((newLine) => (
    <>
      {newLine}
      <br />
    </>
  ));

const textVariantByStatus = {
  [STEP_STATE.DISABLED]: {
    variant: 'neutral',
  },
  [STEP_STATE.COMPLETED]: {
    variant: 'success',
    modifier: 'regular',
  },
  [STEP_STATE.ACTIVE]: {
    variant: 'primary',
    modifier: 'regular',
  },
};

const stepStatus = (progress, type) => {
  if (progress >= PROGRESS_STATE.COMPLETED) {
    return STEP_STATE.COMPLETED;
  }
  if (
    type !== STEP_TYPE.LAST &&
    progress > PROGRESS_STATE.DEFAULT &&
    progress < PROGRESS_STATE.COMPLETED
  ) {
    return STEP_STATE.ACTIVE;
  }
  return STEP_STATE.DISABLED;
};

const StepIcon = ({ status, image, iconSize }) => (
  <Icon
    name={image}
    height={iconSize / 2}
    width={iconSize / 2}
    disabled={status === STEP_STATE.DISABLED}
    variant='neutral'
    modifier='inverted'
  />
);

StepIcon.protoType = {
  status: PropTypes.oneOf([
    STEP_STATE.DISABLED,
    STEP_STATE.ACTIVE,
    STEP_STATE.COMPLETED,
  ]),
};

const StepLabel = ({ status, label, isLandscape }) => (
  <P
    type='4'
    variant={textVariantByStatus[status]?.variant}
    modifier={textVariantByStatus[status]?.modifier}
  >
    {isLandscape ? label : <TextLineSplitter text={label} />}
  </P>
);

StepLabel.propTypes = {
  status: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  isLandscape: PropTypes.bool.isRequired,
};

const Step = ({
  label,
  progress,
  image,
  type,
  isLandscape,
  iconSize,
  totalSteps,
}) => {
  const status = stepStatus(progress, type);
  const isLast = type === STEP_TYPE.LAST;
  return (
    <S.Wrapper
      iconSize={iconSize}
      isLandscape={isLandscape}
      isLast={isLast}
      totalSteps={totalSteps}
    >
      <S.Icon>
        <S.IconWrapper status={status} iconSize={iconSize}>
          <StepIcon status={status} image={image} iconSize={iconSize} />
        </S.IconWrapper>
        <S.Label status={status} isLandscape={isLandscape} iconSize={iconSize}>
          <StepLabel status={status} label={label} isLandscape={isLandscape} />
        </S.Label>
      </S.Icon>
      {!isLast && (
        <S.Progress iconSize={iconSize} isLandscape={isLandscape}>
          <ProgressLine progress={progress} isLandscape={isLandscape} />
        </S.Progress>
      )}
    </S.Wrapper>
  );
};

export default Step;

Step.propTypes = {
  label: PropTypes.string.isRequired,
  progress: PropTypes.number.isRequired,
  image: PropTypes.string.isRequired,
  type: PropTypes.oneOf([STEP_TYPE.LAST, STEP_TYPE.PROGRESS]),
  isLandscape: PropTypes.bool.isRequired,
  iconSize: PropTypes.number.isRequired,
};
